import { useState, useEffect } from "react";
import { usePost } from "./usePost";
import { EngagementVotingMappingRecord } from "../models/engagementVotingMappingModels";
import {
  getMockMappingsForCompany,
  getMockMappingsForEngagement,
} from "../data/mockEngagementVotingMappings";

interface UseEngagementVotingMappingProps {
  companyId?: string;
  engagementRecordId?: string;
}

interface EngagementVotingMappingParams {
  compass_cid?: string;
  engagement_record_id?: string;
}

interface UseEngagementVotingMappingReturn {
  mappingData: EngagementVotingMappingRecord[];
  isLoading: boolean;
  error: unknown;
  getMappingsForEngagement: (
    engagementId: string
  ) => EngagementVotingMappingRecord[];
  getMappingsForVotingRecord: (
    votingRecordId: string
  ) => EngagementVotingMappingRecord[];
  getHighConfidenceMappings: (
    threshold?: number
  ) => EngagementVotingMappingRecord[];
}

export const useEngagementVotingMapping = ({
  companyId,
  engagementRecordId,
}: UseEngagementVotingMappingProps): UseEngagementVotingMappingReturn => {
  const [mappingData, setMappingData] = useState<
    EngagementVotingMappingRecord[]
  >([]);

  const { data, isLoading, error } = usePost<
    EngagementVotingMappingRecord[],
    EngagementVotingMappingParams
  >();

  useEffect(() => {
    // TODO: Replace with actual API endpoint once backend is ready
    // For now, using mock data
    if (companyId) {
      const mockData = getMockMappingsForCompany(companyId);
      setMappingData(mockData);
    } else if (engagementRecordId) {
      const mockData = getMockMappingsForEngagement(engagementRecordId);
      setMappingData(mockData);
    }

    // When API is ready, uncomment:
    // const params: EngagementVotingMappingParams = {};
    // if (companyId) params.compass_cid = companyId;
    // if (engagementRecordId) params.engagement_record_id = engagementRecordId;
    // getMappingData("GET_ENGAGEMENT_VOTING_MAPPING", params);
  }, [companyId, engagementRecordId]);

  useEffect(() => {
    if (data) {
      setMappingData(data);
    }
  }, [data]);

  const getMappingsForEngagement = (
    engagementId: string
  ): EngagementVotingMappingRecord[] => {
    return mappingData.filter(
      (mapping) => mapping.engagement_record_id === engagementId
    );
  };

  const getMappingsForVotingRecord = (
    votingRecordId: string
  ): EngagementVotingMappingRecord[] => {
    return mappingData.filter(
      (mapping) => mapping.voting_record_id === votingRecordId
    );
  };

  const getHighConfidenceMappings = (
    threshold = 0.8
  ): EngagementVotingMappingRecord[] => {
    return mappingData.filter(
      (mapping) => mapping.confidence_score >= threshold
    );
  };

  return {
    mappingData,
    isLoading,
    error,
    getMappingsForEngagement,
    getMappingsForVotingRecord,
    getHighConfidenceMappings,
  };
};
